import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EventIcon from '@mui/icons-material/Event';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import BoltIcon from '@mui/icons-material/Bolt';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ResponsiveModal } from '../common';
import JalaliDatePicker, { toPersianDigits } from '../common/JalaliDatePicker';
import {
  RaffleInput,
  RaffleItem,
  RaffleRule,
  RaffleRuleType,
  RAFFLE_RULE_LABELS,
  RAFFLE_RULE_DESCRIPTIONS,
} from '../../services/raffle.service';
import '../../assets/fonts/lalezar/lalezar.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: RaffleInput) => Promise<void>;
  /** برای ویرایش؛ خالی = ساخت جدید */
  initial?: RaffleItem | null;
}

const GOLD = '#F5B301';

const RULE_ICONS: Record<RaffleRuleType, React.ReactNode> = {
  app_installed: <PhoneIphoneIcon fontSize="small" />,
  signup_range: <EventIcon fontSize="small" />,
  min_purchases: <ShoppingBagIcon fontSize="small" />,
  recent_activity: <BoltIcon fontSize="small" />,
};

const RULE_ORDER: RaffleRuleType[] = ['app_installed', 'signup_range', 'min_purchases', 'recent_activity'];

const emptyRules = (): RaffleRule[] =>
  RULE_ORDER.map((type) => ({
    type,
    multiplier: type === 'app_installed' ? 3 : 2,
    enabled: false,
    params: type === 'min_purchases' ? { minCount: 1 } : type === 'recent_activity' ? { days: 30 } : {},
  }));

interface PrizeRow {
  key: number;
  title: string;
  amount: string;
}

/** رنگ مدال رتبه‌ها؛ رتبهٔ ۴ به بعد خنثی */
const medalColor = (rank: number): string | null =>
  rank === 1 ? GOLD : rank === 2 ? '#9AA5B1' : rank === 3 ? '#C77B30' : null;

const STEPS = [
  { label: 'اطلاعات و استخر', icon: <GroupsIcon sx={{ fontSize: 18 }} /> },
  { label: 'قوانین ضریب شانس', icon: <TuneIcon sx={{ fontSize: 18 }} /> },
  { label: 'جوایز و برنده‌ها', icon: <EmojiEventsIcon sx={{ fontSize: 18 }} /> },
];

let prizeKeySeq = 1;

const RaffleFormModal: React.FC<Props> = ({ open, onClose, onSubmit, initial }) => {
  const theme = useTheme();
  const isEdit = Boolean(initial);
  const [step, setStep] = useState(0);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [signupFrom, setSignupFrom] = useState('');
  const [signupTo, setSignupTo] = useState('');
  const [rules, setRules] = useState<RaffleRule[]>(emptyRules());
  const [excludeAdmins, setExcludeAdmins] = useState(true);
  const [excludeSellers, setExcludeSellers] = useState(true);
  const [showParticipants, setShowParticipants] = useState(false);
  const [prizes, setPrizes] = useState<PrizeRow[]>([{ key: prizeKeySeq++, title: '', amount: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setStep(0);
    if (initial) {
      setTitle(initial.title || '');
      setDescription(initial.description || '');
      setSignupFrom(initial.pool?.signupFrom || '');
      setSignupTo(initial.pool?.signupTo || '');
      setExcludeAdmins(initial.options?.excludeAdmins !== false);
      setExcludeSellers(initial.options?.excludeSellers !== false);
      setShowParticipants(initial.options?.showParticipants === true);
      const base = emptyRules();
      for (const r of initial.rules || []) {
        const idx = base.findIndex((b) => b.type === r.type);
        if (idx >= 0) base[idx] = { ...base[idx], ...r, params: { ...base[idx].params, ...r.params } };
      }
      setRules(base);
      const rows = [...(initial.prizes || [])]
        .sort((a, b) => a.rank - b.rank)
        .map((p) => ({
          key: prizeKeySeq++,
          title: p.title,
          amount: p.amount !== undefined && p.amount !== null ? String(p.amount) : '',
        }));
      setPrizes(rows.length ? rows : [{ key: prizeKeySeq++, title: '', amount: '' }]);
    } else {
      setTitle('');
      setDescription('');
      setSignupFrom('');
      setSignupTo('');
      setExcludeAdmins(true);
      setExcludeSellers(true);
      setShowParticipants(false);
      setRules(emptyRules());
      setPrizes([{ key: prizeKeySeq++, title: '', amount: '' }]);
    }
  }, [open, initial]);

  const validPrizes = useMemo(() => prizes.filter((p) => p.title.trim()), [prizes]);

  const setRule = (type: RaffleRuleType, patch: Partial<RaffleRule>) =>
    setRules((prev) => prev.map((r) => (r.type === type ? { ...r, ...patch } : r)));
  const setRuleParam = (type: RaffleRuleType, patch: Record<string, unknown>) =>
    setRules((prev) => prev.map((r) => (r.type === type ? { ...r, params: { ...r.params, ...patch } } : r)));

  const movePrize = (idx: number, dir: -1 | 1) => {
    setPrizes((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const validateStep = (s: number): string | null => {
    if (s === 0 && !title.trim()) return 'عنوان قرعه‌کشی را وارد کنید';
    if (s === 1) {
      const signupRule = rules.find((r) => r.type === 'signup_range');
      if (signupRule?.enabled && !signupRule.params?.from && !signupRule.params?.to) {
        return 'برای قانون «ثبت‌نام در بازهٔ تاریخ» حداقل یکی از تاریخ‌ها را مشخص کنید';
      }
    }
    if (s === 2 && !validPrizes.length) return 'حداقل یک جایزه با عنوان تعریف کنید';
    return null;
  };

  const goNext = () => {
    const err = validateStep(step);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setStep((v) => Math.min(2, v + 1));
  };

  const handleSubmit = async () => {
    for (let s = 0; s <= 2; s++) {
      const err = validateStep(s);
      if (err) {
        setError(err);
        setStep(s);
        return;
      }
    }
    const input: RaffleInput = {
      title: title.trim(),
      description: description.trim() || undefined,
      pool: { signupFrom: signupFrom || null, signupTo: signupTo || null },
      options: { excludeAdmins, excludeSellers, showParticipants },
      rules,
      prizes: validPrizes.map((p) => ({
        title: p.title.trim(),
        amount: p.amount !== '' ? Number(p.amount) : undefined,
      })),
    };
    setSaving(true);
    try {
      await onSubmit(input);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'خطا در ذخیرهٔ قرعه‌کشی');
    } finally {
      setSaving(false);
    }
  };

  const sectionCard = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 3,
    p: 2,
    bgcolor: 'background.paper',
  } as const;

  // ---------------------------------------------------------------- stepper

  const stepper = (
    <Stack direction="row" alignItems="center" sx={{ mb: 3, mt: 0.5 }}>
      {STEPS.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <React.Fragment key={s.label}>
            {i > 0 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mx: 1,
                  borderRadius: 1,
                  bgcolor: i <= step ? GOLD : 'divider',
                  transition: 'background-color .3s',
                }}
              />
            )}
            <Stack
              alignItems="center"
              spacing={0.5}
              sx={{ cursor: done ? 'pointer' : 'default', minWidth: 72 }}
              onClick={() => done && setStep(i)}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all .3s',
                  ...(active
                    ? { bgcolor: GOLD, color: '#1a1200', boxShadow: `0 0 0 5px ${alpha(GOLD, 0.18)}` }
                    : done
                      ? { bgcolor: alpha(GOLD, 0.15), color: GOLD }
                      : { bgcolor: 'action.hover', color: 'text.disabled' }),
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 18 }} /> : s.icon}
              </Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: active ? 700 : 500, color: active ? 'text.primary' : 'text.secondary' }}
              >
                {s.label}
              </Typography>
            </Stack>
          </React.Fragment>
        );
      })}
    </Stack>
  );

  // ------------------------------------------------------------------ steps

  const step0 = (
    <Stack spacing={2.5}>
      <TextField
        label="عنوان قرعه‌کشی"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        placeholder="مثلاً قرعه‌کشی بزرگ تابستانهٔ کازماشاپ"
        autoFocus
      />
      <TextField
        label="توضیح (اختیاری)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        minRows={2}
      />
      <Box sx={sectionCard}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <GroupsIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">چه کسانی شرکت داده شوند؟</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          پیش‌فرض: همهٔ کاربران فعال (به جز ادمین‌ها). اگر فقط ثبت‌نامی‌های یک بازه باید شرکت کنند، تاریخ بگذارید.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              ثبت‌نام از تاریخ
            </Typography>
            <JalaliDatePicker value={signupFrom} onChange={setSignupFrom} placeholder="بدون محدودیت" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              ثبت‌نام تا تاریخ
            </Typography>
            <JalaliDatePicker value={signupTo} onChange={setSignupTo} placeholder="بدون محدودیت" />
          </Box>
        </Stack>
      </Box>

      <Box sx={sectionCard}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <TuneIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2">تنظیمات قرعه‌کشی</Typography>
        </Stack>
        <Stack spacing={0.5}>
          {[
            {
              icon: <AdminPanelSettingsIcon sx={{ fontSize: 20 }} />,
              label: 'ادمین‌ها در قرعه‌کشی نباشند',
              desc: 'هیچ ادمینی وارد استخر نمی‌شود',
              value: excludeAdmins,
              set: setExcludeAdmins,
            },
            {
              icon: <StorefrontIcon sx={{ fontSize: 20 }} />,
              label: 'فروشنده‌ها در قرعه‌کشی نباشند',
              desc: 'صاحبان فروشگاه وارد استخر نمی‌شوند',
              value: excludeSellers,
              set: setExcludeSellers,
            },
            {
              icon: <VisibilityIcon sx={{ fontSize: 20 }} />,
              label: 'نمایش تعداد شرکت‌کننده‌ها در صحنهٔ نمایش',
              desc: 'اگر خاموش باشد، در نمایش قرعه‌کشی تعداد افراد دیده نمی‌شود',
              value: showParticipants,
              set: setShowParticipants,
            },
          ].map((opt) => (
            <Stack key={opt.label} direction="row" alignItems="center" spacing={1.25} sx={{ py: 0.75 }}>
              <Box sx={{ color: opt.value ? 'primary.main' : 'text.disabled', display: 'flex' }}>{opt.icon}</Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  {opt.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {opt.desc}
                </Typography>
              </Box>
              <Switch checked={opt.value} onChange={(e) => opt.set(e.target.checked)} />
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );

  const step1 = (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: '2px' }} />
        <Typography variant="caption" color="text.secondary">
          شانس پایهٔ همه ۱ است؛ هر قانونِ برقرار، شانس کاربر را در ضریبش ضرب می‌کند. مثلاً کاربری که اپ دارد (×۳) و در
          بازه ثبت‌نام کرده (×۲)، شانسش ۶ برابر است.
        </Typography>
      </Stack>
      {rules.map((rule) => {
        const on = rule.enabled;
        return (
          <Box
            key={rule.type}
            sx={{
              border: '1.5px solid',
              borderColor: on ? alpha(GOLD, 0.55) : 'divider',
              borderRadius: 3,
              p: 2,
              transition: 'all .25s',
              bgcolor: on ? alpha(GOLD, theme.palette.mode === 'dark' ? 0.07 : 0.05) : 'background.paper',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: on ? alpha(GOLD, 0.18) : 'action.hover',
                  color: on ? GOLD : 'text.disabled',
                  transition: 'all .25s',
                }}
              >
                {RULE_ICONS[rule.type]}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700}>
                  {RAFFLE_RULE_LABELS[rule.type]}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {RAFFLE_RULE_DESCRIPTIONS[rule.type]}
                </Typography>
              </Box>
              {on && (
                <TextField
                  size="small"
                  type="number"
                  value={rule.multiplier}
                  onChange={(e) =>
                    setRule(rule.type, { multiplier: Math.min(100, Math.max(1, Number(e.target.value) || 1)) })
                  }
                  sx={{ width: 92 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography sx={{ color: GOLD, fontWeight: 800 }}>×</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <Switch checked={on} onChange={(e) => setRule(rule.type, { enabled: e.target.checked })} />
            </Stack>

            {on && rule.type === 'signup_range' && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    از تاریخ
                  </Typography>
                  <JalaliDatePicker
                    value={rule.params?.from || ''}
                    onChange={(iso) => setRuleParam('signup_range', { from: iso })}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    تا تاریخ
                  </Typography>
                  <JalaliDatePicker
                    value={rule.params?.to || ''}
                    onChange={(iso) => setRuleParam('signup_range', { to: iso })}
                  />
                </Box>
              </Stack>
            )}
            {on && rule.type === 'min_purchases' && (
              <TextField
                label="حداقل تعداد سفارش تحویل‌شده"
                size="small"
                type="number"
                value={rule.params?.minCount ?? 1}
                onChange={(e) => setRuleParam('min_purchases', { minCount: Math.max(1, Number(e.target.value) || 1) })}
                sx={{ width: 230, mt: 1.5 }}
              />
            )}
            {on && rule.type === 'recent_activity' && (
              <TextField
                label="فعال در چند روز اخیر"
                size="small"
                type="number"
                value={rule.params?.days ?? 30}
                onChange={(e) => setRuleParam('recent_activity', { days: Math.max(1, Number(e.target.value) || 1) })}
                sx={{ width: 230, mt: 1.5 }}
                InputProps={{ endAdornment: <InputAdornment position="end">روز</InputAdornment> }}
              />
            )}
          </Box>
        );
      })}
    </Stack>
  );

  const step2 = (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', mt: '2px' }} />
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 380 }}>
            هر جایزه = یک برنده. ترتیب لیست، رتبه را مشخص می‌کند و قرعه‌کشی دقیقاً به همین تعداد برنده انتخاب می‌کند —
            نه بیشتر.
          </Typography>
        </Stack>
        <Chip
          icon={<EmojiEventsIcon sx={{ fontSize: 16 }} />}
          label={`${toPersianDigits(String(validPrizes.length))} برنده`}
          sx={{ fontWeight: 800, bgcolor: alpha(GOLD, 0.15), color: theme.palette.mode === 'dark' ? GOLD : '#8a6400' }}
        />
      </Stack>

      <Stack spacing={1.25}>
        {prizes.map((p, idx) => {
          const rank = idx + 1;
          const mc = medalColor(rank);
          return (
            <Stack
              key={p.key}
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{
                border: '1px solid',
                borderColor: mc ? alpha(mc, 0.5) : 'divider',
                borderRadius: 3,
                p: 1.25,
                bgcolor: mc ? alpha(mc, theme.palette.mode === 'dark' ? 0.08 : 0.05) : 'background.paper',
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Lalezar, Vazirmatn, sans-serif',
                  fontSize: 18,
                  ...(mc
                    ? { background: `linear-gradient(135deg, ${mc}, ${alpha(mc, 0.6)})`, color: '#fff' }
                    : { bgcolor: 'action.hover', color: 'text.secondary' }),
                }}
              >
                {toPersianDigits(String(rank))}
              </Box>
              <TextField
                size="small"
                value={p.title}
                onChange={(e) =>
                  setPrizes((prev) => prev.map((x) => (x.key === p.key ? { ...x, title: e.target.value } : x)))
                }
                sx={{ flex: 1 }}
                placeholder={rank === 1 ? 'مثلاً ۵ میلیون تومان وجه نقد' : 'عنوان جایزه'}
              />
              <TextField
                size="small"
                type="number"
                value={p.amount}
                onChange={(e) =>
                  setPrizes((prev) => prev.map((x) => (x.key === p.key ? { ...x, amount: e.target.value } : x)))
                }
                sx={{ width: 150, display: { xs: 'none', sm: 'block' } }}
                placeholder="مبلغ (تومان)"
              />
              <Stack direction="row" spacing={0}>
                <Tooltip title="بالا" arrow>
                  <span>
                    <IconButton size="small" disabled={idx === 0} onClick={() => movePrize(idx, -1)}>
                      <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="پایین" arrow>
                  <span>
                    <IconButton size="small" disabled={idx === prizes.length - 1} onClick={() => movePrize(idx, 1)}>
                      <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="حذف" arrow>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={prizes.length === 1}
                      onClick={() => setPrizes((prev) => prev.filter((x) => x.key !== p.key))}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Button
        startIcon={<AddIcon />}
        onClick={() => setPrizes((prev) => [...prev, { key: prizeKeySeq++, title: '', amount: '' }])}
        sx={{
          alignSelf: 'flex-start',
          borderRadius: 99,
          px: 2.5,
          border: '1.5px dashed',
          borderColor: alpha(GOLD, 0.6),
          color: theme.palette.mode === 'dark' ? GOLD : '#8a6400',
          '&:hover': { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08) },
        }}
      >
        افزودن جایزه
      </Button>

      <Typography variant="caption" color="text.secondary">
        پرداخت جوایز دستی است؛ مبلغ فقط برای نمایش در صحنهٔ قرعه‌کشی به کار می‌رود.
      </Typography>
    </Stack>
  );

  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'ویرایش قرعه‌کشی' : 'قرعه‌کشی جدید'}
      subtitle="قرعه‌کشی وزنی بین کاربران سایت"
      icon={<CasinoIcon sx={{ color: GOLD }} />}
      maxWidth="md"
      mobileBreakpoint="md"
      disableClose={saving}
      error={error}
      actions={
        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ width: '100%' }}>
          <Button onClick={onClose} disabled={saving} color="inherit">
            انصراف
          </Button>
          <Stack direction="row" spacing={1}>
            {step > 0 && (
              <Button onClick={() => setStep((v) => v - 1)} disabled={saving} variant="outlined" color="inherit">
                مرحلهٔ قبل
              </Button>
            )}
            {step < 2 ? (
              <Button variant="contained" onClick={goNext} sx={{ px: 4, borderRadius: 2 }}>
                مرحلهٔ بعد
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving}
                sx={{
                  px: 4,
                  borderRadius: 2,
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${GOLD}, #ff8f00)`,
                  color: '#1a1200',
                  '&:hover': { background: 'linear-gradient(135deg, #ffc233, #ff9d1c)' },
                }}
              >
                {saving ? 'در حال ذخیره…' : isEdit ? 'ذخیرهٔ تغییرات' : 'ساخت قرعه‌کشی'}
              </Button>
            )}
          </Stack>
        </Stack>
      }
    >
      <Box sx={{ py: 0.5 }}>
        {stepper}
        {step === 0 && step0}
        {step === 1 && step1}
        {step === 2 && step2}
      </Box>
    </ResponsiveModal>
  );
};

export default RaffleFormModal;
